import { Copy, Link2, Share2, Users } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { RecipeView } from '../../../shared/api';
import { APP_NAME } from '../../../shared/brand';
import { addedByLine, sharedByLine } from '../../../shared/sharing';
import { useCreateShareLink, useSession, useShareShowName, useTurnOffShareLink } from '../../api/hooks';
import { dropStaleLink, lastShowName, rememberedLink, rememberLink, rememberShowName } from '../../lib/myShareLinks';
import { useUI } from '../../lib/ui';
import { Sheet } from '../Sheet';

interface Props {
  recipe: RecipeView;
  onClose: () => void;
}

const canShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

/**
 * Share a recipe by link: Show my name, the link to copy or share, New link, Turn off link,
 * and how many people added it (a count, never who). Members who open the link can see the
 * recipe and add a copy. Show my name covers the link and every copy made from the recipe's
 * links, so it works with the link off too. The server keeps only a hash of the link, so its
 * address can be shown only on the device that made it (lib/myShareLinks.ts).
 */
export function ShareRecipeSheet({ recipe, onClose }: Props) {
  const { toast } = useUI();
  const session = useSession();
  const create = useCreateShareLink();
  const setName = useShareShowName();
  const turnOff = useTurnOffShareLink();
  const baseId = useId();
  const ids = { name: `${baseId}-name`, nameHint: `${baseId}-name-hint`, link: `${baseId}-link`, confirm: `${baseId}-confirm` };
  const uid = session.data?.user?.id ?? '';
  const myName = session.data?.user?.name.trim() ?? '';
  const link = recipe.sharing.link;
  // The address, when this device made the link that's on.
  const [made, setMade] = useState<{ linkId: string; url: string } | null>(null);
  const linkId = link?.id ?? null;
  const url = link ? (made?.linkId === link.id ? made.url : rememberedLink(uid, recipe.id, link.id)) : null;
  // The recipe's own choice once it has had a link; before that, your last choice on this device (on at first).
  const saved = recipe.sharing.showName;
  const [nextShowName, setNextShowName] = useState(() => lastShowName());
  const showName = (setName.isPending ? setName.variables?.showName : undefined) ?? saved ?? nextShowName;
  const [confirm, setConfirm] = useState<'new' | 'off' | null>(null);
  const copyRef = useRef<HTMLButtonElement>(null);
  const createRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const busy = create.isPending || turnOff.isPending;

  // A link replaced or turned off (here or on another device) isn't kept on this one.
  useEffect(() => {
    if (uid) dropStaleLink(uid, recipe.id, linkId);
  }, [uid, recipe.id, linkId]);

  const toggleName = () => {
    const next = !showName;
    if (saved === null) {
      rememberShowName(next);
      return setNextShowName(next);
    }
    // Remembered once the server has it: a failed change leaves both as they were.
    setName.mutate({ id: recipe.id, showName: next }, { onSuccess: () => rememberShowName(next), onError: (e) => toast(e.message, { tone: 'error' }) });
  };

  const makeLink = () => {
    if (busy) return;
    create.mutate(
      { id: recipe.id, showName },
      {
        onSuccess: ({ url: address, sharing }) => {
          if (sharing.link) {
            rememberLink(uid, recipe.id, sharing.link.id, address);
            setMade({ linkId: sharing.link.id, url: address });
          }
          if (confirm === 'new') toast('New link made. The old one no longer works.');
          setConfirm(null);
          requestAnimationFrame(() => copyRef.current?.focus());
        },
        onError: (e) => toast(e.message, { tone: 'error' }),
      },
    );
  };

  const stopLink = () => {
    if (busy) return;
    turnOff.mutate(recipe.id, {
      onSuccess: () => {
        setConfirm(null);
        toast('Link turned off');
        // The button that had focus is gone: Create link takes it.
        requestAnimationFrame(() => createRef.current?.focus());
      },
      onError: (e) => toast(e.message, { tone: 'error' }),
    });
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      // No clipboard (an older browser, or permission refused): select it to copy by hand.
      inputRef.current?.select();
      toast('Copy the selected link', { tone: 'error' });
    }
  };

  const shareLink = async () => {
    if (!url) return;
    try {
      await navigator.share({ title: recipe.name, url });
    } catch (err) {
      if (!(err instanceof DOMException && err.name === 'AbortError')) void copy();
    }
  };

  const nameHint = `People with the link or a copy see “${sharedByLine(showName && myName ? myName : null)}”.`;

  return (
    <Sheet title="Share recipe" onClose={onClose}>
      <div className="stack" style={{ gap: 18 }}>
        <p className="small muted">
          Anyone signed in to this {APP_NAME} who opens the link can see <b style={{ color: 'var(--ink)' }}>{recipe.name}</b>, with its notes and original
          link, and add a copy of their own. Nothing else of yours is shared.
        </p>

        <div className="share-name">
          <div className="grow">
            <div id={ids.name} style={{ fontWeight: 600 }}>
              Show my name
            </div>
            <div id={ids.nameHint} className="tiny muted">
              {nameHint}
              {showName && !myName ? ' Add your name in You to show it.' : ''}
            </div>
          </div>
          <button className="switch" role="switch" aria-checked={showName} aria-labelledby={ids.name} aria-describedby={ids.nameHint} onClick={toggleName} />
        </div>

        {!link ? (
          <button ref={createRef} className="btn btn--primary btn--lg btn--block" aria-disabled={busy || undefined} onClick={makeLink}>
            <Link2 size={18} aria-hidden="true" /> Create link
          </button>
        ) : url ? (
          <div className="stack" style={{ gap: 8 }}>
            <label className="field__label" htmlFor={ids.link}>
              Your link
            </label>
            <input ref={inputRef} id={ids.link} className="input share-link" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
            <div className="row wrap">
              <button ref={copyRef} className="btn btn--primary" onClick={() => void copy()}>
                <Copy size={16} aria-hidden="true" /> Copy link
              </button>
              {canShare() && (
                <button className="btn btn--ghost" onClick={() => void shareLink()}>
                  <Share2 size={16} aria-hidden="true" /> Share…
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="small">Your link is on, but this device doesn't have its address. To share from here, make a new link.</p>
        )}

        {(link || recipe.sharing.adds > 0) && (
          <p className="small row" style={{ gap: 8 }}>
            <Users size={16} aria-hidden="true" className="muted" /> {addedByLine(recipe.sharing.adds)}
          </p>
        )}

        {link &&
          (confirm ? (
            // Keyed, so the prompt's buttons are new elements and focus lands on Cancel, never a reused button.
            <div key="confirm" className="stack" style={{ gap: 10 }} role="group" aria-labelledby={ids.confirm}>
              <span id={ids.confirm} className="small">
                {confirm === 'new'
                  ? 'Make a new link? The old one stops working, and copies made from it stop getting your updates.'
                  : 'Turn off this link? It stops working right away, and copies stop getting your updates.'}
              </span>
              <div className="row wrap">
                <button className="btn btn--danger btn--sm" aria-disabled={busy || undefined} onClick={confirm === 'new' ? makeLink : stopLink}>
                  {confirm === 'new' ? 'Make new link' : 'Turn off'}
                </button>
                <button className="btn btn--ghost btn--sm" autoFocus onClick={() => setConfirm(null)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div key="actions" className="row wrap">
              <button className="btn btn--ghost btn--sm" onClick={() => setConfirm('new')}>
                New link
              </button>
              <button className="btn btn--quiet btn--sm" onClick={() => setConfirm('off')}>
                Turn off link
              </button>
            </div>
          ))}
      </div>
    </Sheet>
  );
}
